// Core
import React, { useEffect, useState } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import { RequestMethod, requestVantageApi } from '../../../../../plugins/request';

// Store
import { getUser } from '../../../../../store/session.store';

// Types
import { User, UserType } from '../../../../../types/global';

// Media
import mockedCustomerLogo from '../../../../../media/images/logoSuperior.avif';

// Components
import { Drawer } from '../../../../../layout/elements/Drawer';
import { FileUpload } from '../../../../../layout/elements/FileUpload';
import { ImagePreview } from '../../../../../layout/elements/ImagePreview';
import { Input } from '../../../../../layout/elements/Input';
import { Paragraph, ParagraphWeight } from '../../../../../layout/elements/Paragraph';
import {
  Select,
  SelectSizeType,
  SelectTagsMode,
} from '../../../../../layout/elements/Select';
import { showToast, ToastType } from '../../../../../layout/elements/Toast';
import { AtLine } from '../../../../../layout/icons';

// View
import { UpdateCustomerProps } from './Update.types';
import {
  FileUploadWrapper,
  FormFieldsContainer,
  Image,
  ImageContainer,
  LogoUploadContainer,
  UpdateContainer,
} from './Update.style';

function UpdateCustomer({
  className,
  customer,
  isDrawerOpen,
  onCloseDrawer,
  onUpdateData,
  testId,
}: UpdateCustomerProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  // Getter
  const user: User | null = getUser();

  /* ***********************************************************************************************
  *************************************** LOCAL STATES *********************************************
  *********************************************************************************************** */

  interface LogoFile {
    source?: string;
  }

  interface State {
    isLoading: number | true | null;
    name?: string | number | null;
    domains?: string | string [] | null;
    logoError?: string | null;
    logoFile?: LogoFile | null;
    logoRemoved: boolean;
  }

  const initialState: State = {
    isLoading: null,
    name: customer.name,
    domains: customer.domains,
    logoError: null,
    logoFile: null,
    logoRemoved: false,
  };

  const [state, setState] = useState<State>(initialState);

  /* ***********************************************************************************************
  *********************************************** METHODS ******************************************
  *********************************************************************************************** */

  /* Starts loading the Drawer */
  const startDrawerLoading = (): void => {
    setState((prevState: State): State => ({
      ...prevState,
      isLoading: true,
    }));
  };

  /* Finish loading the Drawer */
  const stopDrawerLoading = (): void => {
    setState((prevState: State): State => ({
      ...prevState,
      isLoading: 100,
    }));
  };

  /* Update customer data */
  const onUpdate = async (): Promise<void> => {
    startDrawerLoading();

    try {
      await requestVantageApi.customer({
        method: RequestMethod.PUT,
        path: `/customer/${customer.id}`,
        body: {
          name: state.name,
          domains: state.domains,
          logoUpload: state.logoRemoved ? null : state.logoFile?.source,
        },
      });

      showToast({
        text: t('view.customers.update.customerUpdated', { customer: customer.name }),
        type: ToastType.success,
      });

      stopDrawerLoading();

      setTimeout((): void => {
        onUpdateData();
        onCloseDrawer();
      }, 300);
    } catch (error: unknown) {
      setState((prevState: State): State => ({ ...prevState, isLoading: null }));
    }
  };

  /* Trigger onFieldChange based on fieldId */
  const handleFieldChange = (fieldId: string, value: string | string[]): void => {
    setState((prevState: State): State => ({
      ...prevState,
      [fieldId]: value,
    }));
  };

  /* Handle logo file selection */
  const handleLogoUpload = (file: LogoFile): void => {
    setState((prevState: State): State => ({
      ...prevState,
      logoError: null,
      logoFile: file,
      logoRemoved: false, // Reset removal flag when new logo is uploaded
    }));
  };

  /* Handle logo removal from file upload component */
  const handleLogoRemove = (): void => {
    setState((prevState: State): State => ({
      ...prevState,
      logoError: null,
      logoFile: null,
      logoRemoved: false, // Reset removal flag when file is removed from upload
    }));
  };

  /* Handle logo upload error */
  const handleLogoError = (errorMessage: string): void => {
    setState((prevState: State): State => ({
      ...prevState,
      logoError: errorMessage,
    }));
  };

  /* Handle logo removal when customer has existing logo */
  const handleRemoveExistingLogo = (): void => {
    setState((prevState: State): State => ({
      ...prevState,
      logoFile: null,
      logoRemoved: true, // Mark as removed to send null to backend
    }));
  };

  /* ***********************************************************************************************
  **************************************** COMPONENT HANDLING **************************************
  *********************************************************************************************** */

  /* Reset state when drawer closes or customer changes */
  useEffect((): void => {
    if (isDrawerOpen) {
      setState({
        isLoading: null,
        name: customer.name,
        domains: customer.domains,
        logoError: null,
        logoFile: null,
        logoRemoved: false,
      });
    }
  }, [
    isDrawerOpen,
    customer.id,
    customer.name,
    customer.domains,
  ]);

  /* Check if there are any changes compared to the original customer data */
  const hasChanges = (): boolean => {
    const nameChanged: boolean = state.name !== customer.name;
    const currentDomains: string[] = Array.isArray(state.domains) ? state.domains : [];
    const originalDomains: string[] = customer.domains || [];
    const domainsChanged: boolean = currentDomains.length !== originalDomains.length
      || currentDomains.some((domain: string, index: number) => domain !== originalDomains[index]);
    const logoChanged: boolean = state.logoFile !== null || state.logoRemoved === true;
    return nameChanged || domainsChanged || logoChanged;
  };

  return (
    <Drawer
      testId={testId}
      className={className}
      title={`${t('common.edit')}`}
      open={isDrawerOpen}
      onClose={onCloseDrawer}
      primaryAction={
        user?.type === UserType.service
          ? undefined
          : {
            children: t('common.edit'),
            onClick: onUpdate,
            disabled: !hasChanges() || !state.name || !state.domains?.length || !!state.logoError,
            loading: state.isLoading,
            loadingText: `${t('common.editing')}`,
          }
      }
      secondaryAction={{ children: t('common.cancel'), onClick: onCloseDrawer }}
    >
      <UpdateContainer>
        {((customer.logoUrl && !state.logoRemoved && !state.logoFile) || state.logoFile) ? (
          <ImagePreview
            image={{
              name: customer.name,
              source: state.logoFile?.source || (customer.logoUrl || mockedCustomerLogo),
            }}
            onRemove={
              customer.logoUrl && !state.logoFile
                ? handleRemoveExistingLogo
                : handleLogoRemove
            }
            testId="customer-logo-preview"
          />
        ) : (
          <ImageContainer>
            <Image
              data-testid="customer-logo-preview"
              src={mockedCustomerLogo}
              alt={customer.name}
            />
          </ImageContainer>
        )}
        <LogoUploadContainer>
          <Paragraph weight={ParagraphWeight.bold}>
            {t('view.customers.update.logo')}
          </Paragraph>
          <FileUploadWrapper>
            <FileUpload
              id="customer-logo-upload"
              file={state.logoFile || { source: undefined }}
              onUpload={handleLogoUpload}
              onRemove={handleLogoRemove}
              onError={handleLogoError}
              maxSize={1 * 1024 * 1024} // 1MB
              error={!!state.logoError}
              errorText={state.logoError}
              supportingText={`${t('view.customers.update.supportingText')}`}
              accept=".jpg, .png, .gif, .jpeg, .avif, .webp"
            />
          </FileUploadWrapper>
        </LogoUploadContainer>
        <div>
          <Paragraph
            testId="main-info-section"
            weight={ParagraphWeight.bold}
          >
            {t('common.mainInfo')}
          </Paragraph>
        </div>
        <FormFieldsContainer>
          <div>
            <Input
              placeholder={`${t('common.name')}`}
              label={`${t('common.name')}`}
              value={state.name}
              onChange={(newValue) => {
                if (typeof newValue === 'string') {
                  handleFieldChange('name', newValue);
                }
              }}
              maxLength={65}
              testId="input-customer-name"
            />
          </div>
          <div>
            <Select
              label={`${t('view.customers.update.domain')}`}
              mode={SelectTagsMode.tags}
              placeholder={`${t('view.customers.update.placeholderDomain')}`}
              onChange={(value: string | string[]) => handleFieldChange('domains', value)}
              size={SelectSizeType.lg}
              value={state.domains ? state.domains : undefined}
              supportingText={`${t('view.customers.update.domainsSupportingText')}`}
              prefix={<AtLine />}
            />
          </div>
        </FormFieldsContainer>
      </UpdateContainer>
    </Drawer>
  );
}

export { UpdateCustomer };
